import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Alert, Snackbar,
  Switch, FormControlLabel, CircularProgress, TextField, Chip,
  FormControl, InputLabel, Select, MenuItem, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import { apiService } from '../../services/apiService';

const CalibrationSettingsTab = ({ observatoryId, observatoryName }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const [systemDefaults, setSystemDefaults] = useState({});
  const [detectedCameras, setDetectedCameras] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const extractApiError = (err, fallback) => {
    const e = err?.response?.data?.error;
    if (typeof e === 'string') return e;
    if (e && typeof e === 'object' && typeof e.message === 'string') return e.message;
    return fallback;
  };

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [rebuildDialogOpen, setRebuildDialogOpen] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [expireDarksDialog, setExpireDarksDialog] = useState({ open: false, camera_name: '', readout_mode: '', loading: false });

  useEffect(() => {
    if (!observatoryId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      try {
        const policyResponse = await apiService.getCalibrationPolicy(observatoryId);
        if (cancelled) return;
        setSettings(policyResponse.data?.observatory_settings || {});
        setSystemDefaults(policyResponse.data?.system_defaults || {});
        setDetectedCameras(policyResponse.data?.detected_cameras || []);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load calibration settings:', error);
          setNotification({ open: true, message: 'Failed to load settings', severity: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [observatoryId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiService.updateCalibrationPolicy(observatoryId, settings);
      setSettings(response.data || settings);
      setNotification({ open: true, message: 'Settings saved successfully', severity: 'success' });
    } catch (error) {
      setNotification({ open: true, message: extractApiError(error, 'Failed to save settings'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const updateNestedSetting = (parent, key, value) => {
    setSettings(prev => ({ ...prev, [parent]: { ...(prev[parent] || {}), [key]: value } }));
  };

  const updateCameraConfigs = (cam, field, value) => {
    setSettings(prev => {
      const configs = { ...(prev.camera_configs || {}) };
      for (const key of cam.keys) {
        configs[key] = { ...(configs[key] || { camera_type: prev.camera_type || 'cmos' }), [field]: value };
      }
      return { ...prev, camera_configs: configs };
    });
  };

  const getCameraConfig = (cam) => {
    const configs = settings.camera_configs || {};
    for (const key of cam.keys) {
      if (configs[key]) return configs[key];
    }
    return {};
  };

  if (!observatoryId) {
    return <Alert severity="info">Select an observatory to view calibration settings.</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* General */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>General</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enabled ?? systemDefaults.calibration_enabled ?? false}
                    onChange={(e) => updateSetting('enabled', e.target.checked)}
                  />
                }
                label="Enable Calibration"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                When enabled, light frames will be automatically calibrated using available master frames.
                System default: {systemDefaults.calibration_enabled ? 'Enabled' : 'Disabled'}
              </Typography>
              <FormControl sx={{ minWidth: 200, mt: 1 }}>
                <InputLabel>Default Camera Type</InputLabel>
                <Select
                  value={settings.camera_type || 'cmos'}
                  label="Default Camera Type"
                  onChange={(e) => updateSetting('camera_type', e.target.value)}
                >
                  <MenuItem value="cmos">CMOS</MenuItem>
                  <MenuItem value="ccd">CCD</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                CCD cameras use bias subtraction and dark scaling. CMOS cameras use time-matched darks.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Dark Capture Window — per-camera shutter/filter moved into the Camera
            section below (issue #625). */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Dark Capture Window</Typography>
              <TextField
                label="Dark Max Sun Altitude"
                type="number"
                value={settings.dark_max_sun_altitude_degrees ?? -18}
                onChange={(e) => updateSetting('dark_max_sun_altitude_degrees', parseFloat(e.target.value))}
                size="small"
                inputProps={{ min: -90, max: 90, step: 1 }}
                helperText="Maximum sun altitude (degrees) at or below which dark capture is allowed (use a positive value to permit daytime darks with a dark filter / capped scope)"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Temperature */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Temperature Matching</Typography>
              <TextField
                label="Temperature Tolerance (°C)"
                type="number"
                value={settings.temperature_tolerance_celsius ?? 5}
                onChange={(e) => updateSetting('temperature_tolerance_celsius', parseFloat(e.target.value))}
                size="small"
                fullWidth
                inputProps={{ min: 0.5, max: 20, step: 0.5 }}
                helperText="Maximum temperature difference between light and calibration frames"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Per-Camera Set Points */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Camera Temperature Set Points</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Frames taken outside these set points (± tolerance) are treated as uncalibratable.
                Leave empty to accept any temperature.
              </Typography>
              {detectedCameras.length === 0 ? (
                <Alert severity="info">
                  No cameras detected yet. Cameras appear here once calibration masters have been created.
                </Alert>
              ) : (
                detectedCameras.map((cam) => {
                  const config = getCameraConfig(cam);
                  const setPoints = config.temperature_set_points || [];
                  return (
                    <Box key={cam.camera_name} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>{cam.camera_name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {setPoints.map((temp, idx) => (
                          <Chip
                            key={idx}
                            label={`${temp}°C`}
                            onDelete={() => {
                              const updated = setPoints.filter((_, i) => i !== idx);
                              updateCameraConfigs(cam, 'temperature_set_points', updated.length > 0 ? updated : undefined);
                            }}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="-10"
                            sx={{ width: 80 }}
                            inputProps={{ step: 5, min: -60, max: 30 }}
                            id={`temp-input-${cam.camera_name}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && !setPoints.includes(val)) {
                                  updateCameraConfigs(cam, 'temperature_set_points', [...setPoints, val].sort((a, b) => a - b));
                                  e.target.value = '';
                                }
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const input = document.getElementById(`temp-input-${cam.camera_name}`);
                              const val = parseFloat(input?.value);
                              if (!isNaN(val) && !setPoints.includes(val)) {
                                updateCameraConfigs(cam, 'temperature_set_points', [...setPoints, val].sort((a, b) => a - b));
                                input.value = '';
                              }
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Camera Type</InputLabel>
                          <Select
                            value={config.camera_type || settings.camera_type || 'cmos'}
                            label="Camera Type"
                            onChange={(e) => updateCameraConfigs(cam, 'camera_type', e.target.value)}
                          >
                            <MenuItem value="cmos">CMOS</MenuItem>
                            <MenuItem value="ccd">CCD</MenuItem>
                          </Select>
                        </FormControl>
                        {cam.readout_modes?.length > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => setExpireDarksDialog({
                              open: true,
                              camera_name: cam.camera_name,
                              readout_mode: cam.readout_modes[0],
                              readout_modes: cam.readout_modes,
                              loading: false,
                            })}
                          >
                            Expire Darks...
                          </Button>
                        )}
                      </Box>

                      {/* Default readout mode for new observations on this camera. */}
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <InputLabel>Default Readout Mode</InputLabel>
                          <Select
                            value={config.default_readout_mode || ''}
                            label="Default Readout Mode"
                            onChange={(e) => updateCameraConfigs(cam, 'default_readout_mode', e.target.value || null)}
                          >
                            <MenuItem value=""><em>— no default —</em></MenuItem>
                            {(cam.readout_modes || []).map((m) => (
                              <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                          </Select>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            Pre-fills the readout mode field on new observations using this camera.
                            Leave blank if the user should pick each time.
                          </Typography>
                        </FormControl>
                      </Box>
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Frame Age Limits */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Frame Age Limits</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Maximum age before frames are considered expired. Warnings show within the warning window.
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'Darks', max: 'dark_max_age_days', warn: 'dark_expiry_warning_days', defMax: 90, defWarn: 7 },
                  { label: 'Flats', max: 'flat_max_age_days', warn: 'flat_expiry_warning_days', defMax: 30, defWarn: 3 },
                  { label: 'Bias', max: 'bias_max_age_days', warn: 'bias_expiry_warning_days', defMax: 180, defWarn: 14 },
                ].map((g) => (
                  <Grid item xs={12} sm={4} key={g.label}>
                    <Typography variant="subtitle2" gutterBottom>{g.label}</Typography>
                    <TextField
                      label="Max Age (days)"
                      type="number"
                      value={settings[g.max] ?? g.defMax}
                      onChange={(e) => updateSetting(g.max, parseInt(e.target.value))}
                      size="small"
                      fullWidth
                      sx={{ mb: 1 }}
                      inputProps={{ min: 1, max: 365 }}
                    />
                    <TextField
                      label="Warning Window (days)"
                      type="number"
                      value={settings[g.warn] ?? g.defWarn}
                      onChange={(e) => updateSetting(g.warn, parseInt(e.target.value))}
                      size="small"
                      fullWidth
                      inputProps={{ min: 1, max: 30 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Master Creation */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Master Creation</Typography>
              <TextField
                label="Minimum Frames for Master"
                type="number"
                value={settings.min_frames_for_master ?? 10}
                onChange={(e) => updateSetting('min_frames_for_master', parseInt(e.target.value))}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                inputProps={{ min: 1, max: 100 }}
                helperText="Minimum individual frames required to create a master"
              />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Stacking Method</InputLabel>
                <Select
                  value={settings.stacking_method || 'sigma_clip'}
                  label="Stacking Method"
                  onChange={(e) => updateSetting('stacking_method', e.target.value)}
                >
                  <MenuItem value="mean">Mean</MenuItem>
                  <MenuItem value="median">Median</MenuItem>
                  <MenuItem value="sigma_clip">Sigma Clipped Mean</MenuItem>
                </Select>
              </FormControl>
              {(settings.stacking_method || 'sigma_clip') === 'sigma_clip' && (
                <>
                  <TextField
                    label="Sigma Clip Value"
                    type="number"
                    value={settings.sigma_clip_value ?? 2.5}
                    onChange={(e) => updateSetting('sigma_clip_value', parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                    inputProps={{ min: 1, max: 5, step: 0.1 }}
                  />
                  <TextField
                    label="Sigma Clip Iterations"
                    type="number"
                    value={settings.sigma_clip_iterations ?? 5}
                    onChange={(e) => updateSetting('sigma_clip_iterations', parseInt(e.target.value))}
                    size="small"
                    fullWidth
                    inputProps={{ min: 1, max: 20 }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quality Gates */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quality Gates (ADU)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reject incoming frames whose median ADU falls outside these thresholds.
                Leave blank to disable.
              </Typography>
              <Grid container spacing={2}>
                {[
                  { key: 'flat_min_adu', label: 'Flat Min ADU', placeholder: 'e.g. 15000', helper: 'Reject flats below this' },
                  { key: 'flat_max_adu', label: 'Flat Max ADU', placeholder: 'e.g. 45000', helper: 'Reject flats above this' },
                  { key: 'dark_max_adu', label: 'Dark Max ADU', placeholder: 'e.g. 500', helper: 'Reject darks above this' },
                  { key: 'bias_max_adu', label: 'Bias Max ADU', placeholder: 'e.g. 300', helper: 'Reject bias above this' },
                ].map((g) => (
                  <Grid item xs={6} key={g.key}>
                    <TextField
                      label={g.label}
                      type="number"
                      size="small"
                      fullWidth
                      value={settings[g.key] ?? ''}
                      onChange={(e) => updateSetting(g.key, e.target.value ? Number(e.target.value) : null)}
                      placeholder={g.placeholder}
                      helperText={g.helper}
                    />
                  </Grid>
                ))}
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                Typical flat median ADU: 20,000–40,000. Darks and bias should be near read noise.
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Stale Master Policy */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Stale Master Policy</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2, mt: 1 }}>
                <InputLabel>Stale Master Action</InputLabel>
                <Select
                  value={settings.stale_master_action || 'use_with_warning'}
                  label="Stale Master Action"
                  onChange={(e) => updateSetting('stale_master_action', e.target.value)}
                >
                  <MenuItem value="use_silently">Use Silently</MenuItem>
                  <MenuItem value="use_with_warning">Use with Warning</MenuItem>
                  <MenuItem value="skip">Skip (Don't Calibrate)</MenuItem>
                </Select>
              </FormControl>
              <Alert severity="info">
                {settings.stale_master_action === 'skip'
                  ? 'Calibration will be skipped if any required master is stale.'
                  : settings.stale_master_action === 'use_silently'
                  ? 'Stale masters will be used without warning.'
                  : 'Stale masters will be used; a warning is attached to the calibrated frame.'}
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Rotator */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Rotator Settings</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.has_rotator ?? false}
                    onChange={(e) => updateSetting('has_rotator', e.target.checked)}
                  />
                }
                label="Has Rotator"
              />
              {(settings.has_rotator ?? false) && (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 2, mt: 2 }}>
                    <InputLabel>Rotation-Matched Flats</InputLabel>
                    <Select
                      value={settings.require_rotation_matched_flats || 'preferred'}
                      label="Rotation-Matched Flats"
                      onChange={(e) => updateSetting('require_rotation_matched_flats', e.target.value)}
                    >
                      <MenuItem value="required">Required</MenuItem>
                      <MenuItem value="preferred">Preferred (fallback to any)</MenuItem>
                      <MenuItem value="ignore">Ignore Rotation</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Rotation Tolerance (degrees)"
                    type="number"
                    value={settings.rotation_tolerance_degrees ?? 1.0}
                    onChange={(e) => updateSetting('rotation_tolerance_degrees', parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Retention */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Retention</Typography>
              <TextField
                label="Master Retention (days)"
                type="number"
                value={settings.master_retention_days ?? 365}
                onChange={(e) => updateSetting('master_retention_days', parseInt(e.target.value))}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                inputProps={{ min: 30, max: 3650 }}
                helperText="Days to keep superseded/expired masters before deletion"
              />
              <TextField
                label="Individual Retention (days)"
                type="number"
                value={settings.individual_retention_days ?? 30}
                onChange={(e) => updateSetting('individual_retention_days', parseInt(e.target.value))}
                size="small"
                fullWidth
                inputProps={{ min: 7, max: 365 }}
                helperText="Days to keep individual frames after master creation"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Automation */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Automation</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>External Storage Sync</InputLabel>
                <Select
                  value={settings.external_storage_sync || 'raw_only'}
                  label="External Storage Sync"
                  onChange={(e) => updateSetting('external_storage_sync', e.target.value)}
                >
                  <MenuItem value="raw_only">Raw Only</MenuItem>
                  <MenuItem value="calibrated_only">Calibrated Only</MenuItem>
                  <MenuItem value="both">Both Raw and Calibrated</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Calibration Needs Lookback (days)"
                type="number"
                value={settings.calibration_needs_lookback_days ?? 30}
                onChange={(e) => updateSetting('calibration_needs_lookback_days', parseInt(e.target.value))}
                size="small"
                fullWidth
                inputProps={{ min: 7, max: 365 }}
                helperText="How far back to look when calculating calibration needs"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Gap Filling */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Gap Filling</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Automatically capture dark and bias frames during gaps in the observation schedule.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.gap_filling?.enabled || false}
                    onChange={(e) => updateNestedSetting('gap_filling', 'enabled', e.target.checked)}
                  />
                }
                label="Enable Gap Filling"
              />
              {settings.gap_filling?.enabled && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {[
                    { key: 'setup_overhead_seconds', label: 'Setup Overhead (s)', def: 30, min: 0, max: 300, helper: 'Time to prepare for calibration capture' },
                    { key: 'teardown_overhead_seconds', label: 'Teardown Overhead (s)', def: 60, min: 0, max: 300, helper: 'Time to restore science-ready state' },
                    { key: 'safety_buffer_seconds', label: 'Safety Buffer (s)', def: 60, min: 0, max: 300, helper: 'Extra time before next observation' },
                    { key: 'min_useful_gap_seconds', label: 'Min Useful Gap (s)', def: 120, min: 30, max: 3600, helper: 'Gaps shorter than this are skipped' },
                    { key: 'per_frame_overhead_seconds', label: 'Per-Frame Overhead (s)', def: 5, min: 0, max: 60, helper: 'Download/readout time per frame' },
                    { key: 'max_frames_per_session', label: 'Max Frames Per Session', def: 50, min: 1, max: 200, helper: 'Cap frames per gap-fill session' },
                  ].map((f) => (
                    <Grid item xs={6} key={f.key}>
                      <TextField
                        fullWidth size="small" type="number"
                        label={f.label}
                        value={settings.gap_filling?.[f.key] ?? f.def}
                        onChange={(e) => updateNestedSetting('gap_filling', f.key, parseInt(e.target.value) || f.def)}
                        helperText={f.helper}
                        InputProps={{ inputProps: { min: f.min, max: f.max } }}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Interrupt Priority</InputLabel>
                      <Select
                        value={settings.gap_filling?.interrupt_priority || 'any'}
                        label="Interrupt Priority"
                        onChange={(e) => updateNestedSetting('gap_filling', 'interrupt_priority', e.target.value)}
                      >
                        <MenuItem value="any">Any observation interrupts</MenuItem>
                        <MenuItem value="medium_plus">Medium+ priority only</MenuItem>
                        <MenuItem value="high_only">High priority only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Danger Zone */}
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid', borderColor: 'error.main' }}>
            <CardContent>
              <Typography variant="h6" color="error" gutterBottom>Danger Zone</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reset all calibration data for this observatory, or delete only masters and let the
                sweep restack them. Reset cannot be undone.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setResetDialogOpen(true)}
                sx={{ mr: 2 }}
              >
                Reset Calibration Data
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setRebuildDialogOpen(true)}
              >
                Rebuild Masters
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                <strong>Reset:</strong> deletes everything (individuals, masters, sessions).{' '}
                <strong>Rebuild Masters:</strong> deletes only masters; individuals are preserved and
                the sweep restacks within 5 minutes.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Expire Darks Dialog */}
      <Dialog
        open={expireDarksDialog.open}
        onClose={() => !expireDarksDialog.loading && setExpireDarksDialog({ ...expireDarksDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Expire Dark Masters</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Expire all active dark masters for <strong>{expireDarksDialog.camera_name}</strong> with the selected readout mode.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Readout Mode</InputLabel>
            <Select
              value={expireDarksDialog.readout_mode}
              label="Readout Mode"
              onChange={(e) => setExpireDarksDialog(prev => ({ ...prev, readout_mode: e.target.value }))}
              disabled={expireDarksDialog.loading}
            >
              {(expireDarksDialog.readout_modes || []).map(rm => (
                <MenuItem key={rm} value={rm}>{rm}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="warning">
            Expired darks won't be used for calibrating new images. Gap-filling will acquire replacements.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpireDarksDialog({ ...expireDarksDialog, open: false })} disabled={expireDarksDialog.loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={expireDarksDialog.loading}
            startIcon={expireDarksDialog.loading && <CircularProgress size={16} />}
            onClick={async () => {
              setExpireDarksDialog(prev => ({ ...prev, loading: true }));
              try {
                const result = await apiService.expireDarks(observatoryId, {
                  camera_name: expireDarksDialog.camera_name,
                  readout_mode: expireDarksDialog.readout_mode,
                });
                setNotification({
                  open: true,
                  message: result.data?.message || `Expired ${result.data?.expired || 0} dark master(s)`,
                  severity: 'success',
                });
                setExpireDarksDialog({ open: false, camera_name: '', readout_mode: '', loading: false });
              } catch (error) {
                setNotification({ open: true, message: extractApiError(error, 'Failed to expire darks'), severity: 'error' });
                setExpireDarksDialog(prev => ({ ...prev, loading: false }));
              }
            }}
          >
            {expireDarksDialog.loading ? 'Expiring...' : 'Expire Darks'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation */}
      <Dialog open={resetDialogOpen} onClose={() => !resetting && setResetDialogOpen(false)}>
        <DialogTitle>Reset Calibration Data</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete all calibration data for <strong>{observatoryName || 'this observatory'}</strong>. Cannot be undone.
          </Alert>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>All individual calibration frames (bias, dark, flat)</li>
            <li>All master frames</li>
            <li>All stacking jobs</li>
            <li>Gap-filling and flat-capture sessions</li>
            <li>Associated MinIO storage objects</li>
            <li>Master frame cache</li>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)} disabled={resetting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={resetting}
            startIcon={resetting ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
            onClick={async () => {
              setResetting(true);
              try {
                const result = await apiService.resetCalibration(observatoryId);
                const data = result.data || {};
                const parts = [];
                if (data.calibrationFrames) parts.push(`${data.calibrationFrames} frames`);
                if (data.minioObjects?.deleted) parts.push(`${data.minioObjects.deleted} files`);
                if (data.calibrationJobs) parts.push(`${data.calibrationJobs} jobs`);
                if (data.gapFillingSessions) parts.push(`${data.gapFillingSessions} gap-fill sessions`);
                const summary = parts.length ? `Deleted: ${parts.join(', ')}` : 'Reset complete (no data to delete)';
                setNotification({ open: true, message: summary, severity: 'success' });
                setResetDialogOpen(false);
              } catch (error) {
                setNotification({ open: true, message: extractApiError(error, 'Reset failed'), severity: 'error' });
              } finally {
                setResetting(false);
              }
            }}
          >
            {resetting ? 'Resetting...' : 'Reset All Calibration Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rebuild Confirmation */}
      <Dialog open={rebuildDialogOpen} onClose={() => !rebuilding && setRebuildDialogOpen(false)}>
        <DialogTitle>Rebuild Masters</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Deletes all master frames for <strong>{observatoryName || 'this observatory'}</strong>.
            Individuals are preserved — sweep restacks within 5 minutes.
          </Alert>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>All master frames deleted (DB + S3)</li>
            <li>Consumed source frames released back to active</li>
            <li>Master disk cache orphans pruned</li>
            <li>Individual frames preserved</li>
            <li>Next sweep restacks masters with the current scheme</li>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebuildDialogOpen(false)} disabled={rebuilding}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={rebuilding}
            startIcon={rebuilding ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
            onClick={async () => {
              setRebuilding(true);
              try {
                const result = await apiService.rebuildMasters(observatoryId);
                const data = result.data || {};
                const parts = [];
                if (data.mastersDeleted) parts.push(`${data.mastersDeleted} masters deleted`);
                if (data.sourceFramesReleased) parts.push(`${data.sourceFramesReleased} sources released`);
                if (data.cacheFilesRemoved) parts.push(`${data.cacheFilesRemoved} cache files pruned`);
                if (data.s3Errors) parts.push(`${data.s3Errors} S3 errors`);
                const summary = parts.length
                  ? `Rebuild complete: ${parts.join(', ')}. Sweep will restack within 5 minutes.`
                  : 'No masters to rebuild';
                setNotification({ open: true, message: summary, severity: 'success' });
                setRebuildDialogOpen(false);
              } catch (error) {
                setNotification({ open: true, message: extractApiError(error, 'Rebuild failed'), severity: 'error' });
              } finally {
                setRebuilding(false);
              }
            }}
          >
            {rebuilding ? 'Rebuilding...' : 'Rebuild Masters'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {typeof notification.message === 'string' ? notification.message : String(notification.message ?? '')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CalibrationSettingsTab;
